"use client";

import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "next/navigation";

import InteractionSection from "./sections/InteractionSection";
import MobileSection from "./sections/MobileSection";
import DetailSection from "./sections/DetailSection";
import PlayerSection from "./sections/PlayerSection";
import { socketUpdateProgressV2 } from "@/socket";
import SocketContext from "@/socket/Context";
import Article from "./partials/article";
import services from "@/store/services";
import Components from "@/components";
import "./player-side.scss";
import api from "@/api";

function PlayerSide(props) {
  const { socket } = useContext(SocketContext).SocketState;
  const user = useSelector((state) => state?.user?.users);
  const { articleInfo, courseInfo } = useSelector((state) => state.video);
  const enrollments = useSelector((state) => state.enrollment);
  const { _id } = useSelector((state) => state.user.users);
  const [isLoading, setIsLoading] = useState(true);
  const [isWarning, setIsWarning] = useState({
    show: false,
    type: "",
    name: "",
    btnClick: "",
  });
  const dispatch = useDispatch();
  const params = useParams();

  const { video } = params;

  // ----------------- On Video Change or Play ------------------

  const fetchStageContent = useCallback(async () => {
    setIsLoading(true);
    const response = await api.getStageContentV2(video, "course");

    if (response.status === 200) {
      if (response?.data?.result?.type === "article") {
        dispatch(
          services.updateVideoInfo({
            articleInfo: response.data?.result,
            videoInfo: null,
          })
        );
      } else if (response?.data?.result?.type === "video") {
        dispatch(
          services.updateVideoInfo({
            videoInfo: response.data?.result,
            articleInfo: null,
            playrate: 1,
            isStarted: false,
            haveAssignment: response.data?.result?.assignment ? true : false,
          })
        );
      }
      setIsLoading(false);
    }
  }, [video]);

  const updateArticleProgress = useCallback(() => {
    if (articleInfo) {
      socketUpdateProgressV2(
        {
          uid: _id,
          eid: articleInfo?._id,
          cid: articleInfo?.courseId,
        },
        socket
      );
    }
  }, [articleInfo]);

  function updateEnrollment() {
    const isEnrolled = user?.email
      ? enrollments.length > 0 &&
        enrollments.find((x) => x.courseId === courseInfo?._id)
      : false;

    if (isEnrolled && Object.keys(isEnrolled).length) {
      dispatch(
        services.updateEnrollmentStatus({
          isEnrolled,
        })
      );
    }
  }

  const onVideoComplete = () => {
    socket?.on("course:videoComplete", () => {
      dispatch(services.fetchEnrollment(user));
      setIsLoading(false);
    });

    return () => {
      socket.off("course:videoComplete");
    };
  };

  useEffect(() => {
    fetchStageContent();
  }, [fetchStageContent]);

  useEffect(() => {
    updateEnrollment();
  }, [enrollments]);

  useEffect(() => {
    onVideoComplete();
  }, [socket]);

  return (
    <>
      {isWarning.show ? (
        <Components.WarningAlert
          type={isWarning.type}
          onbtnClick={isWarning}
          name={isWarning.name}
          onCancel={() => setIsWarning((prev) => ({ ...prev, show: false }))}
        />
      ) : null}

      <>
        {articleInfo === null ? (
          <PlayerSection
            tour={props.tour}
            loading={isLoading}
            isSrcChanged={props.isSrcChanged}
            changeIsSrcChange={props.changeIsSrcChange}
          />
        ) : (
          <Article
            _id={articleInfo._id}
            data={articleInfo.description}
            updateProgress={updateArticleProgress}
            isAccessable={articleInfo?.isAccessable}
          />
        )}

        <br className={"separator"} />
        <InteractionSection
          handleWarning={(fields) =>
            setIsWarning((prev) => ({ ...prev, ...fields }))
          }
          isLoading={isLoading}
        />
        <section className={"big-screen"}>
          <DetailSection
            handleWarning={(fields) =>
              setIsWarning((prev) => ({ ...prev, ...fields }))
            }
            isLoading={isLoading}
          />
        </section>
        <section className={"small-screen"}>
          <MobileSection />
        </section>
      </>
    </>
  );
}

export default React.memo(PlayerSide);
